import { useQuery } from "@tanstack/react-query";
import { Copy, Gauge, History, Layers, LayoutDashboard, LogOut, Menu as MenuIcon, Moon, PanelLeftClose, PanelLeftOpen, Radar, Settings, Sun, UsersRound, X, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { t, tn, useLang } from "../i18n";
import { api } from "../services/api";
import type { Attention } from "../services/types";
import { useAuth, useCan } from "../services/auth";
import { useActiveJobs } from "../services/events";
import { ErrorBoundary } from "./ErrorBoundary";

export const APP_NAME = "DomainOps Hub";
type NavItem = { to: string; label: string; icon: LucideIcon; match: string[]; perm: string };
// One entry per tool; sub-features live in tabs inside each module (Domain Drop Monitor: search / watchlist;
// Wayback Recovery: domains / job history / downloads). Old URLs still resolve (see App.tsx).
const NAV: { group: string; items: NavItem[] }[] = [
  { group: "Main", items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, match: [], perm: "dashboard.view" }] },
  { group: "Domain tools", items: [
    { to: "/hunter/search", label: "Domain Drop Monitor", icon: Radar, match: ["/hunter"], perm: "drops.view" },
    { to: "/checker", label: "DA/PA Checker", icon: Gauge, match: ["/checker"], perm: "checker.use" },
  ] },
  { group: "Recovery tools", items: [
    { to: "/domains", label: "Wayback Recovery", icon: History, match: ["/domains", "/jobs", "/batches"], perm: "recovery.view" },
    { to: "/tools/single-page", label: "Website Cloner", icon: Copy, match: ["/tools"], perm: "cloner.view" },
  ] },
  { group: "System", items: [
    { to: "/settings", label: "Settings", icon: Settings, match: ["/settings"], perm: "" },
    { to: "/users", label: "User Management", icon: UsersRound, match: ["/users"], perm: "users.view" },
  ] },
];
const isActive = (item: NavItem, path: string) => item.to === "/" ? path === "/" : item.match.some((m) => path === m || path.startsWith(m + "/"));
const NAV_LINK = (active: boolean, collapsed: boolean) =>
  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${collapsed ? "lg:justify-center lg:px-0" : ""} ${active ? "bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-100" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"}`;

function readCollapsed() {
  try {
    return localStorage.getItem("adr-sidebar") === "collapsed";
  } catch {
    return false;
  }
}

function readTheme(): "light" | "dark" {
  try {
    const t = localStorage.getItem("adr-theme");
    if (t === "light" || t === "dark") return t;
  } catch {
    /* storage unavailable */
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function useTheme() {
  const [theme, setTheme] = useState<"light" | "dark">(readTheme);
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    try {
      localStorage.setItem("adr-theme", theme);
    } catch {
      /* ignore */
    }
  }, [theme]);
  return { theme, toggle: () => setTheme((t) => (t === "dark" ? "light" : "dark")) };
}

function LangSwitch() {
  const { lang, setLang } = useLang();
  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-xs font-semibold dark:border-slate-700" role="radiogroup" aria-label={t("Language")}>
      {(["en", "id"] as const).map((l) => (
        <button key={l} type="button" role="radio" aria-checked={lang === l} onClick={() => setLang(l)} title={l === "en" ? "English" : "Bahasa Indonesia"}
          className={`rounded-md px-2 py-1 uppercase ${lang === l ? "bg-brand-600 text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

export function Layout() {
  const { state, logout } = useAuth();
  const attention = useQuery({ queryKey: ["attention"], queryFn: () => api.get<Attention>("/api/system/attention"), refetchInterval: 30000 });
  const a = attention.data;
  const urgent = a ? a.expiring.filter((e) => e.hours_left <= 6).length + a.stuck.length : 0;
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const location = useLocation();
  const { jobs } = useActiveJobs();
  const can = useCan();
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter((i) => !i.perm || can(i.perm)) })).filter((g) => g.items.length);
  useEffect(() => setOpen(false), [location.pathname]);
  const current = NAV.flatMap((g) => g.items).find((i) => isActive(i, location.pathname));
  useEffect(() => {
    document.title = current ? `${t(current.label)} · ${APP_NAME}` : APP_NAME;
  }, [current]);
  useEffect(() => {
    try {
      localStorage.setItem("adr-sidebar", collapsed ? "collapsed" : "expanded");
    } catch {
      /* ignore */
    }
  }, [collapsed]);
  const running = jobs?.length ?? 0;
  const username = state.status === "authenticated" ? state.username : "";
  // The collapsed (icon) rail applies to desktop only; the mobile drawer always shows labels.
  const hide = collapsed ? "lg:hidden" : "";

  return (
    <div className="min-h-screen lg:flex">
      <a href="#main" className="skip-link">{t("Skip to content")}</a>
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 transform flex-col border-r border-slate-200 bg-white transition-[transform,width] dark:border-slate-800 dark:bg-slate-900 lg:sticky lg:top-0 lg:h-screen lg:shrink-0 lg:translate-x-0 ${collapsed ? "lg:w-16" : ""} ${open ? "translate-x-0" : "-translate-x-full"}`}
        aria-label={t("Main navigation")}
      >
        <div className={`flex h-16 items-center gap-2.5 px-5 ${collapsed ? "lg:justify-center lg:px-0" : ""}`}>
          <div className="rounded-lg bg-brand-600 p-1.5 text-white"><Layers className="h-4 w-4" /></div>
          <div className={`leading-tight ${hide}`}>
            <div className="text-sm font-semibold">{APP_NAME}</div>
            <div className="text-xs text-slate-500">{t("Domain intelligence & recovery")}</div>
          </div>
          <button className="btn-ghost ml-auto px-2 lg:hidden" onClick={() => setOpen(false)} aria-label={t("Close menu")}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {nav.map(({ group, items }) => (
            <div key={group} className="mb-3">
              <div className={`px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 ${hide}`}>{t(group)}</div>
              {collapsed && <div className="mx-2 mb-2 hidden border-t border-slate-100 dark:border-slate-800 lg:block" />}
              <div className="space-y-1">
                {items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item, location.pathname);
                  return (
                    <NavLink key={item.to} to={item.to} end={item.to === "/"} className={NAV_LINK(active, collapsed)} aria-current={active ? "page" : undefined}
                      aria-label={t(item.label)} title={collapsed ? t(item.label) : undefined}>
                      <Icon className="h-4 w-4 shrink-0" /> <span className={hide}>{t(item.label)}</span>
                      {item.to === "/" && urgent > 0 && <span className={`ml-auto rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white ${hide}`} title={t("Needs attention")}>{urgent}</span>}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <Link to="/jobs?filter=active" title={collapsed ? t("Jobs") : undefined}
          className={`mx-3 mb-2 rounded-xl border border-slate-200 p-3 text-xs hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${collapsed ? "lg:flex lg:justify-center lg:p-2" : ""}`}>
          <div className="flex items-center gap-2 font-medium">
            <span className={`h-2 w-2 shrink-0 rounded-full ${running ? "animate-pulse bg-brand-500" : "bg-slate-300 dark:bg-slate-600"}`} />
            <span className={hide}>{jobs === null ? t("Connecting…") : running ? tn(running, "{n} job in progress", "{n} jobs in progress") : t("No jobs running")}</span>
          </div>
          <div className={`mt-0.5 text-slate-500 ${hide}`}>{t("Jobs keep running when you close this page.")}</div>
        </Link>
        <div className={`flex items-center gap-2 border-t border-slate-200 p-3 dark:border-slate-800 ${collapsed ? "lg:flex-col" : ""}`}>
          <Link to="/settings?tab=general" className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1 py-1 hover:bg-slate-100 dark:hover:bg-slate-800" title={t("Profile")}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold uppercase text-brand-700 dark:bg-brand-900/60 dark:text-brand-100">{username.slice(0, 1) || "?"}</span>
            <span className={`min-w-0 leading-tight ${hide}`}>
              <span className="block truncate text-sm font-medium">{username}</span>
              <span className="block text-xs text-slate-500">{t("Profile")}</span>
            </span>
          </Link>
          <button className="btn-ghost px-2" onClick={() => logout()} aria-label={t("Sign out")} title={t("Sign out")}>
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-slate-900/30 backdrop-blur-sm lg:hidden" onClick={() => setOpen(false)} />}

      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b border-slate-200 bg-white/80 px-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/80 sm:px-6">
          <button className="btn-ghost px-2 lg:hidden" onClick={() => setOpen(true)} aria-label={t("Open menu")}>
            <MenuIcon className="h-5 w-5" />
          </button>
          <button className="btn-ghost hidden px-2 lg:inline-flex" onClick={() => setCollapsed((c) => !c)} aria-label={collapsed ? t("Expand sidebar") : t("Collapse sidebar")} title={collapsed ? t("Expand sidebar") : t("Collapse sidebar")}>
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
          <div className="ml-auto flex items-center gap-1">
            <LangSwitch />
            <button className="btn-ghost px-2.5" onClick={toggle} aria-label={t("Toggle theme")} title={theme === "dark" ? t("Light theme") : t("Dark theme")}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </header>
        <main id="main" tabIndex={-1} className="mx-auto w-full max-w-7xl px-4 py-6 outline-none sm:px-6 lg:py-8">
          <ErrorBoundary scope="section" resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

/** Tabs that are separate routes inside one module (e.g. Wayback Recovery: domains / job history / downloads). */
export function ModuleTabs({ label, tabs }: { label: string; tabs: { to: string; label: string; active: boolean }[] }) {
  return (
    <nav className="mb-5 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-slate-200 dark:border-slate-800" aria-label={label}>
      {tabs.map((tab) => (
        <Link key={tab.to} to={tab.to} aria-current={tab.active ? "page" : undefined}
          className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition ${tab.active ? "border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-200" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}

export function WaybackTabs() {
  const { pathname, search } = useLocation();
  const downloads = pathname === "/jobs" && new URLSearchParams(search).get("filter") === "completed";
  return <ModuleTabs label={t("Wayback Recovery")} tabs={[
    { to: "/domains", label: t("Domains"), active: pathname.startsWith("/domains") },
    { to: "/jobs", label: t("Job history"), active: pathname === "/jobs" && !downloads },
    { to: "/jobs?filter=completed", label: t("Downloads"), active: downloads },
  ]} />;
}
